import { useMemo } from "react";

import {
  useCustomWorkbooksQuery,
  useOrganizationByIdQuery,
  useProductCatalog,
  useStandardWorkbooksQuery,
} from "core/queries";

/**
 * Dependency hooks for data-dependent feature flags.
 *
 * Each hook loads exactly the async data its flag's `isEnabled` predicate needs
 * and returns `{ deps, isResolving }`. FeatureProvider calls every one of them
 * unconditionally and in a fixed order, so they must never be called
 * conditionally themselves.
 *
 * `enabled` is the flag's release-stage gate: when it is false the flag cannot
 * be on for this organization, so the fetch is skipped entirely.
 *
 * `isResolving` means "this predicate's inputs have not loaded yet" — deliberately
 * derived from the data itself rather than from react-query status flags, which
 * are muddied by placeholderData.
 */

// Module-level so the reference is stable across renders.
const selectRows = (data) => data?.rows ?? [];

export const useAnalyticsHubFlagDependencies = ({
  organizationId,
  enabled,
}) => {
  const standardWorkbooks = useStandardWorkbooksQuery({
    organizationId,
    enabled,
    select: selectRows,
  });
  const customWorkbooks = useCustomWorkbooksQuery({
    organizationId,
    enabled,
    select: selectRows,
  });

  const standard = standardWorkbooks.data;
  const custom = customWorkbooks.data;

  return useMemo(
    () => ({
      deps: { standardWorkbooks: standard, customWorkbooks: custom },
      isResolving:
        !!enabled && (standard === undefined || custom === undefined),
    }),
    [standard, custom, enabled]
  );
};

export const useManagedProvidersFlagDependencies = ({
  organizationId,
  enabled,
}) => {
  // useOrganizationByIdQuery has no `enabled` option; withholding the id is the
  // supported way to keep it from firing.
  const organization = useOrganizationByIdQuery({
    organizationId: enabled ? organizationId : undefined,
    include: "licenses",
  });
  const { catalog } = useProductCatalog();

  const license = organization.data?.licenses;

  return useMemo(
    () => ({
      deps: { license, catalog },
      isResolving:
        !!enabled && (license === undefined || catalog === undefined),
    }),
    [license, catalog, enabled]
  );
};
